import { useState, useEffect, useCallback, useRef } from "react";
import {
  MapPin, Phone, Mail, Menu, X, Wifi, Car, Utensils, Dumbbell,
  Waves, Sparkles, ShieldCheck, Coffee, Users, Dog, Bed, Baby,
  Building2, Bath, ConciergeBell, Gamepad2, Maximize, ChevronLeft,
  ChevronRight, Eye, Star, Mountain, Sunset, TreePine, ArrowLeft,
  Zap, Droplets, Wind, Tv, RefrigeratorIcon, ShowerHead,
  Thermometer, FlameKindling, BellRing
} from "lucide-react";

import { roomImages } from "../../data/roomImages";
import { logoimages } from "../../data/logoimages";

// import Mirangehero from '../../assets/roomsimages/Miransh/heowebfinal.webp'
// Remove this import:
// import MirangeheroMobile from '../../assets/roomsimages/Miransh/heroweb.webp'

// Use the URL directly as a constant:
// const MirangeheroMobile = "https://res.cloudinary.com/dzkkaxjj8/image/upload/f_auto,q_auto/IMG_0264_km81w7";
// import Mirangelogo from '../../assets/logos/webp/Miranshwhitecrop.webp'

import { Helmet } from "react-helmet";

/* ─────────────────────────────────────────────────────────────────
   GLOBAL STYLES  (injected once)
───────────────────────────────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("__miransh_styles__")) {
  const style = document.createElement("style");
  style.id = "__miransh_styles__";
  style.textContent = `
    @keyframes shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .img-shimmer {
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
      background-size: 200% 100%;
      animation: shimmer 1.4s ease-in-out infinite;
      pointer-events: none;
      border-radius: inherit;
    }
  `;
  document.head.appendChild(style);
}

/* ─────────────────────────────────────────────────────────────────
   PRELOAD HELPER  — preloads an image url, returns a Promise
───────────────────────────────────────────────────────────────── */
function preloadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(src);
    const img = new window.Image();
    img.onload = img.onerror = () => resolve(src);
    img.src = src;
  });
}

/* ─────────────────────────────────────────────────────────────────
   useSlideshow  — bug-free, never shows a blank frame
   Strategy:
     • `active`    = the index we WANT to show (may not be loaded yet)
     • `displayed` = the last fully-loaded active index  (always safe to paint)
     • transitions happen only once the incoming slide is loaded
───────────────────────────────────────────────────────────────── */
function useSlideshow(images = [], interval = 3000, autoPlay = true) {
  const [active,     setActive]     = useState(0);
  const [displayed,  setDisplayed]  = useState(0);   // safe index to paint
  const [loadedMap,  setLoadedMap]  = useState({});
  const [paused,     setPaused]     = useState(false);

  const imagesKey = images.join("|");

  // ── Preload all images and mark each as ready ──────────────────
  useEffect(() => {
    if (!images.length) return;
    setActive(0);
    setDisplayed(0);
    setLoadedMap({});

    images.forEach((src, i) => {
      preloadImage(src).then(() => {
        setLoadedMap(prev => ({ ...prev, [i]: true }));
      });
    });
 
  }, [imagesKey]);

  // ── Once the active slide finishes loading → promote it ────────
  useEffect(() => {
    if (loadedMap[active]) {
      setDisplayed(active);
    }
  }, [active, loadedMap]);

  // ── Auto-advance (only move to the NEXT loaded slide) ──────────
  const activeRef  = useRef(active);
  const pausedRef  = useRef(paused);
  activeRef.current = active;
  pausedRef.current = paused;

  useEffect(() => {
    if (!autoPlay || images.length <= 1) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setActive(prev => {
        // pick next loaded slide; fall back to any slide after one lap
        const len = images.length;
        for (let attempt = 1; attempt <= len; attempt++) {
          const next = (prev + attempt) % len;
          if (loadedMap[next] !== undefined) {
            // even if not loaded yet, advance—displayed won't jump until ready
            return next;
          }
        }
        return (prev + 1) % len;
      });
    }, interval);
    return () => clearInterval(id);

  }, [autoPlay, images.length, interval, imagesKey]);

  const goTo = useCallback((i) => {
    setActive(i);
    if (loadedMap[i]) setDisplayed(i);
  }, [loadedMap]);

  const firstLoaded = !!loadedMap[0];

  return { active, displayed, loadedMap, firstLoaded, paused, setPaused, goTo };
}

/* ─────────────────────────────────────────────────────────────────
   SlideshowImage  — single slide layer, pure opacity transition
   Uses `displayed` (not `active`) for opacity so no blank frames.
───────────────────────────────────────────────────────────────── */
const SlideshowImage = ({ src, alt, index, displayed, loading = "lazy" }) => {
  const isShown = displayed === index;
  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      decoding="async"
      style={{
        position:   "absolute",
        inset:       0,
        width:      "100%",
        height:     "100%",
        objectFit:  "cover",
        opacity:    isShown ? 1 : 0,
        transition: "opacity 0.75s ease-in-out",
        zIndex:     isShown ? 1 : 0,
        willChange: "opacity",
      }}
    />
  );
};

/* ─────────────────────────────────────────────────────────────────
   Shimmer  — visible until first image loads
───────────────────────────────────────────────────────────────── */
const Shimmer = ({ visible }) => (
  <div
    className="img-shimmer"
    style={{
      opacity:    visible ? 1 : 0,
      transition: "opacity 0.5s ease",
      zIndex:     2,
    }}
  />
);

/* ─────────────────────────────────────────────────────────────────
   ROOM FACILITIES
───────────────────────────────────────────────────────────────── */
const ROOM_FACILITIES = [
  { icon: Zap,               label: "Electric Kettle" },
  { icon: ShowerHead,        label: "Toiletries" },
  { icon: BellRing,          label: "Room Service" },
  { icon: Wind,              label: "Air Conditioner" },
  { icon: Tv,                label: "Television" },
  { icon: RefrigeratorIcon,  label: "Refrigerator" },
];

/* ─────────────────────────────────────────────────────────────────
   ROOMS DATA
───────────────────────────────────────────────────────────────── */
const cuddleImages       = roomImages.miransh.cuddleCove;
const MountainMistImages = roomImages.miransh.mountainMist;
const SageWhisperImages  = roomImages.miransh.sageWhisper;
const GoldenGladeImages  = roomImages.miransh.goldenGlade;
const thehavenImages     = roomImages.miransh.theHeaven;
const galleryImages      = roomImages.miransh.gallery;
const Mirangehero = roomImages.miransh.heroimages[0];
const MirangeheroMobile = roomImages.miransh.heroimages[1];
const Mirangelogo = logoimages.logos.Miransh;

export const ROOMS = [
  {
    id: 1,
    name: "Cuddle Cove",
    subtitle: "Honeymoon Suite",
    desc: "Romantic honeymoon suite designed for couples with cozy ambiance and privacy.",
    price: "5,555",
    priceNum: 5555,
    badge: "Couple Special",
    image: [cuddleImages[1], cuddleImages[2], cuddleImages[4]],
    sharing: "2 Guests",
    includes: "Breakfast Included",
    features: [
      { icon: Bed,      text: "King Bed" },
      { icon: Sparkles, text: "Honeymoon Setup" },
    ],
    detailImages: cuddleImages,
    highlights: ["Sea Facing View", "Romantic Ambience", "Modern Amenities", "Breakfast Included"],
    longDesc: `Our Cuddle Cove Honeymoon Suite is assured to provide you with a memorable stay with the sensuous ambience it encompasses. The room's ambience is made romantic along with all the modern amenities to make the stay of the couple a comfortable one. Enjoy a stunning sea-facing view directly from the suite — making every morning and evening unforgettable.`,
    view: "Sea Facing View",
    beds: "King Bed",
    guests: "2",
    size: "Honeymoon Suite",
    facilities: ROOM_FACILITIES,
  },
  {
    id: 2,
    name: "Mountain Mist",
    subtitle: "Luxury Suite with Jacuzzi",
    desc: "Premium luxury suite with private jacuzzi, circular bed, and scenic pool & mountain views.",
    price: "7,777",
    priceNum: 7777,
    badge: "Luxury",
    image: [MountainMistImages[1], MountainMistImages[2], MountainMistImages[3]],
    sharing: "2-3 Guests",
    includes: "Breakfast Included",
    features: [
      { icon: Bath, text: "Private Jacuzzi" },
      { icon: Bed,  text: "Circular King Bed" },
    ],
    detailImages: MountainMistImages,
    highlights: ["Private Jacuzzi", "Circular Bed", "Pool & Mountain View", "Breakfast Included"],
    longDesc: `With a classic circular bed and many other premium facilities, the Mountain Mist Suite brings both the sweetness of honey and the pleasantness of the moon. Enjoy intimate conversations in the generous space, and indulge in the private Jacuzzi in the room with a breathtaking Pool and Mountain View.`,
    view: "Pool & Mountain View",
    beds: "Circular King Bed",
    guests: "2–3",
    size: "Luxury Suite",
    facilities: ROOM_FACILITIES,
  },
  {
    id: 3,
    name: "Sage Whisper",
    subtitle: "Family Room (Triple Sharing)",
    desc: "Nature-inspired suite with calming green tones, natural light, and stunning west-facing sea views for sunsets.",
    price: "7,777",
    priceNum: 7777,
    badge: "Family",
    image: [SageWhisperImages[1], SageWhisperImages[2], SageWhisperImages[3]],
    sharing: "3 Guests",
    includes: "Breakfast Included",
    features: [
      { icon: Users, text: "3 Guests" },
      { icon: Bed,   text: "Triple Bed" },
    ],
    detailImages: SageWhisperImages,
    highlights: ["West-Facing Sea View", "Evening Sunsets", "Natural Textures", "Breakfast Included"],
    longDesc: `Sage Whisper focuses on comfort and sustainability, utilizing calming shades of green, plants, and natural textures to create a relaxing, environmentally conscious atmosphere. Our Suites have stunning west-facing sea views designed perfectly for viewing breathtaking evening sunsets.`,
    view: "West-Facing Sea View",
    beds: "3 Beds",
    guests: "3",
    size: "Family Room",
    facilities: ROOM_FACILITIES,
  },
  {
    id: 4,
    name: "The Haven",
    subtitle: "Luxury Dormitory (8 Sharing) — 1st Floor",
    desc: "Modern-day heirloom quality dormitory with lush green mountain and pool-facing views for 8 guests.",
    price: "9,999",
    priceNum: 9999,
    badge: "Group Stay",
    image: [thehavenImages[1], thehavenImages[2], thehavenImages[3]],
    sharing: "8 Guests",
    includes: "Breakfast Included",
    features: [
      { icon: Users, text: "8 Sharing" },
      { icon: Bed,   text: "Dorm Beds" },
    ],
    detailImages: thehavenImages,
    highlights: ["Mountain & Pool View", "8 Persons", "Shared Luxury", "Breakfast Included"],
    longDesc: `The Haven Dormitory on the 1st Floor features a modern-day heirloom quality of service and environment, making it suitable for travelers seeking both shared living and upscale comfort. The suite comes with a lushy green Mountain and Pool facing view to bring a relaxing comfort to your stay.`,
    view: "Mountain & Pool View",
    beds: "8 Dorm Beds",
    guests: "8",
    size: "Luxury Dormitory",
    facilities: ROOM_FACILITIES,
  },
  {
    id: 5,
    name: "Golden Glade",
    subtitle: "Family Suite (6 Bedded)",
    desc: "Peaceful mountain-facing family suite with panoramic forested views, balcony, and a bunk bed for 2 families.",
    price: "8,888",
    priceNum: 8888,
    badge: "Best for Families",
    image: [GoldenGladeImages[1], GoldenGladeImages[2], GoldenGladeImages[3]],
    sharing: "6 Guests",
    includes: "Breakfast Included",
    features: [
      { icon: Bed,   text: "6 Beds + Bunk" },
      { icon: Users, text: "2 Families" },
    ],
    detailImages: GoldenGladeImages,
    highlights: ["Mountain Panorama", "Private Balcony", "Bunk Bed for Kids", "Breakfast Included"],
    longDesc: `Golden Glade is typically situated away from urban noise, offering a deeply peaceful environment. The mountain-facing room offers panoramic views of forested slopes through large windows or from a private balcony, creating a truly tranquil atmosphere. This Luxury Family Suite accommodates 2 families with 2 children.`,
    view: "Mountain Panorama",
    beds: "6 Beds + Bunk Bed",
    guests: "6",
    size: "Family Suite",
    facilities: ROOM_FACILITIES,
  },
];

/* ─────────────────────────────────────────────────────────────────
   AMENITIES
───────────────────────────────────────────────────────────────── */
export const AMENITIES = [
  { icon: Building2,     name: "Banquet Hall",         desc: "Perfect venue for weddings, events and celebrations." },
  { icon: Utensils,      name: "In-house Restaurant",  desc: "Delicious multi-cuisine food with authentic flavors." },
  { icon: Waves,         name: "Swimming Pool",        desc: "Relax and unwind in our clean and refreshing pool." },
  { icon: Gamepad2,      name: "Game Zone",            desc: "Indoor games and entertainment for all ages." },
  { icon: ConciergeBell, name: "Front Desk",           desc: "24/7 assistance for a smooth and comfortable stay." },
  { icon: Coffee,        name: "Room Service",         desc: "Quick and convenient service directly to your room." },
  { icon: Wifi,          name: "Free WiFi",            desc: "High-speed internet available throughout the property." },
  { icon: Car,           name: "Ample Parking",        desc: "Safe and spacious parking for all guests." },
  { icon: Dog,           name: "Pet Friendly",         desc: "Bring your pets along for a joyful stay." },
  { icon: Baby,          name: "Kids Playing Area",    desc: "Fun and safe play area for children." },
];

const STATS = [
  { number: "50+",  label: "Luxury Rooms" },
  { number: "15+",  label: "Years Experience" },
  { number: "10K+", label: "Happy Guests" },
  { number: "4.9",  label: "Guest Rating" },
];

/* ─────────────────────────────────────────────────────────────────
   ROOM DETAIL PAGE
───────────────────────────────────────────────────────────────── */
const RoomDetailPage = ({ room, onClose, onBook }) => {
  const {
    displayed, active, loadedMap, firstLoaded, paused, setPaused, goTo,
  } = useSlideshow(room.detailImages, 3500);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => setVisible(true), 10);
    return () => { clearTimeout(t); document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const handlePopState = () => { setVisible(false); setTimeout(onClose, 380); };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [onClose]);

  const handleClose = () => {
    setVisible(false);
    if (window.history.state?.detailOpen) window.history.back();
    else setTimeout(onClose, 380);
  };

  return (
    <div
      className="room-detail-overlay"
      style={{
        opacity:   visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
      }}
    >
      <button className="detail-back-btn" onClick={handleClose}>
        <ArrowLeft size={18} />
        <span>Back to Rooms</span>
      </button>

      <div className="room-detail-inner">
        {/* ── LEFT: Gallery ── */}
        <div className="detail-gallery">
          <div
            className="detail-main-img-wrap"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <Shimmer visible={!firstLoaded} />

            {room.detailImages.map((src, i) => (
              <SlideshowImage
                key={src || i}
                src={src}
                alt={`${room.name} ${i + 1}`}
                index={i}
                displayed={displayed}
                loading={i === 0 ? "eager" : "lazy"}
              />
            ))}

            <span className="detail-badge">{room.badge}</span>

            <div className="detail-img-dots">
              {room.detailImages.map((_, i) => (
                <button
                  key={i}
                  className={`detail-dot${active === i ? " active" : ""}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="detail-thumbs">
            {room.detailImages.map((src, i) => (
              <button
                key={i}
                className={`detail-thumb${active === i ? " active" : ""}`}
                onClick={() => goTo(i)}
                style={{ position: "relative", overflow: "hidden" }}
              >
                {!loadedMap[i] && (
                  <div className="img-shimmer" style={{ position: "absolute", inset: 0 }} />
                )}
                <img
                  src={src}
                  alt={`${room.name} thumb ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: "100%", height: "100%", objectFit: "cover", display: "block",
                    opacity: loadedMap[i] ? 1 : 0,
                    transition: "opacity 0.4s ease",
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Info ── */}
        <div className="detail-info">
          <p className="detail-section-tag">Accommodations</p>
          <h2 className="detail-room-name">{room.name}</h2>
          <p className="detail-room-subtitle">{room.subtitle}</p>

          <div className="detail-stats-row">
            <div className="detail-stat"><Bed   size={16} /><span>{room.beds}</span></div>
            <div className="detail-stat"><Users size={16} /><span>{room.guests} Guests</span></div>
            <div className="detail-stat"><MapPin size={16} /><span>{room.view}</span></div>
            <div className="detail-stat"><Coffee size={16} /><span>{room.includes}</span></div>
          </div>

          <p className="detail-long-desc">{room.longDesc}</p>

          <div className="detail-highlights">
            <h4 className="detail-highlights-title">Room Highlights</h4>
            <div className="detail-highlights-grid">
              {room.highlights.map((h, i) => (
                <div className="detail-highlight-item" key={i}>
                  <span className="detail-highlight-dot" />
                  {h}
                </div>
              ))}
            </div>
          </div>

          {room.facilities?.length > 0 && (
            <div className="detail-facilities">
              <h4 className="detail-facilities-title">Room Facilities</h4>
              <div className="detail-facilities-grid">
                {room.facilities.map((fac, i) => (
                  <div className="detail-facility-item" key={i}>
                    <div className="detail-facility-icon"><fac.icon size={18} /></div>
                    <span className="detail-facility-label">{fac.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="detail-features-row">
            {room.features.map((f, i) => (
              <div className="detail-feature-chip" key={i}>
                <f.icon size={15} /><span>{f.text}</span>
              </div>
            ))}
          </div>

          <div className="detail-price-cta">
            <div className="detail-price-block">
              <span className="detail-price-label">Starting from</span>
              <span className="detail-price">&#8377;{room.price}</span>
              <span className="detail-price-per">/ night · incl. breakfast</span>
            </div>
            <button
              className="detail-book-btn"
              onClick={() => { handleClose(); setTimeout(() => onBook(room.name), 400); }}
            >
              Book This Room
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   NAVBAR
───────────────────────────────────────────────────────────────── */
const Navbar = ({ onBookClick }) => {
  const [scrolled,        setScrolled]        = useState(false);
  const [mobileOpen,      setMobileOpen]      = useState(false);
  const [showCallOptions, setShowCallOptions] = useState(false);
  const callDropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll       = () => setScrolled(window.scrollY > 50);
    const handleClickOutside = (e) => {
      if (callDropdownRef.current && !callDropdownRef.current.contains(e.target)) {
        setShowCallOptions(false);
      }
    };
    window.addEventListener("scroll",       handleScroll,       { passive: true });
    window.addEventListener("click",        handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("click",  handleClickOutside);
    };
  }, []);

  const navLinks = [
    { href: "#home",      label: "Home" },
    { href: "#rooms",     label: "Rooms" },
    { href: "#amenities", label: "Amenities" },
    { href: "#gallery",   label: "Gallery" },
    { href: "#contact",   label: "Contact" },
  ];

  const phoneNumbers = [
    { label: "+91 9404617122", href: "tel:+919404617122" },
    { label: "+91 9404662828", href: "tel:+919404662828" },
  ];

  return (
    <>
      <nav className={`nav-container ${scrolled ? "scrolled" : ""}`}>
        <a href="#home" className="nav-logo">
          <img src={Mirangelogo} alt="Miransh Heavenly" className="nav-logo-img" />
        </a>

        <ul className="nav-links">
          {navLinks.map((link) => (
            <li key={link.href}><a href={link.href}>{link.label}</a></li>
          ))}
        </ul>

        <div className="nav-right-actions">
          <div className="nav-call-wrap" ref={callDropdownRef}>
            <button
              className="nav-call-btn"
              onClick={(e) => { e.stopPropagation(); setShowCallOptions(p => !p); }}
              aria-label="Call us"
            >
              <Phone size={17} />
            </button>
            {showCallOptions && (
              <div className="nav-call-dropdown">
                <div className="nav-call-dropdown-header">Call Us</div>
                {phoneNumbers.map((num) => (
                  <a key={num.href} href={num.href} className="nav-call-option">
                    <span className="nav-call-option-icon"><Phone size={13} /></span>
                    {num.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          <button className="nav-book-btn" onClick={onBookClick}>Book Now</button>
        </div>

        <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)}>
          <Menu size={40} />
        </button>
      </nav>

      <div className={`mobile-nav ${mobileOpen ? "open" : ""}`}>
        <button className="mobile-close-btn" onClick={() => setMobileOpen(false)}>
          <X size={32} />
        </button>
        {navLinks.map((link) => (
          <a key={link.href} href={link.href} onClick={() => setMobileOpen(false)}>
            {link.label}
          </a>
        ))}
        <button
          className="btn-primary"
          onClick={() => { setMobileOpen(false); onBookClick(); }}
        >
          Book Now
        </button>
        <div className="mobile-nav-phones">
          {phoneNumbers.map((num) => (
            <a key={num.href} href={num.href} className="mobile-nav-phone-link">
              <Phone size={14} />
              {num.label}
            </a>
          ))}
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────
   HERO  — blur-up technique for smooth background reveal
───────────────────────────────────────────────────────────────── */
const HeroSection = ({ onBookClick }) => {
  const [bgLoaded, setBgLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Preload correct image
  useEffect(() => {
    setBgLoaded(false);
    const src = isMobile ? MirangeheroMobile : Mirangehero;
    const img = new window.Image();
    img.onload = () => setBgLoaded(true);
    img.src = src;
  }, [isMobile]);

  const bgImage = isMobile ? MirangeheroMobile : Mirangehero;

  return (
    <section
      className="hero-section"
      id="home"
      style={{
        position: "relative",
        height: "100vh",
        minHeight: "600px",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="hero-bg"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          filter: bgLoaded ? "none" : "blur(12px)",
          opacity: bgLoaded ? 1 : 0.5,
          transition: "filter 0.9s ease, opacity 0.9s ease, transform 0.9s ease",
          transform: bgLoaded ? "scale(1)" : "scale(1.04)",
        }}
      />
      <div className="hero-overlay" />
      <div className="hero-content">
        <p className="hero-welcome">Welcome to</p>
        <h1 className="hero-title">Miransh Heavenly</h1>
        <p className="hero-subtitle">Where Comfort Meets Serenity</p>
        <div className="hero-buttons">
          <a href="#rooms" className="btn-primary">Explore Our Rooms</a>
          <button className="btn-outline" onClick={onBookClick}>
            Make a Reservation
          </button>
        </div>
      </div>
      <div className="hero-scroll">
        <span>Scroll</span>
        <div className="scroll-line" />
      </div>
    </section>
  );
};
/* ─────────────────────────────────────────────────────────────────
   ROOM CARD
───────────────────────────────────────────────────────────────── */
const RoomCard = ({ room, onBook, onViewDetail }) => {
  const cardImages = room.detailImages?.length > 0
    ? room.detailImages.slice(0, 4)
    : room.image;

  const { displayed, active, firstLoaded, paused, setPaused, goTo } =
    useSlideshow(cardImages, 3200);

  return (
    <div
      className="room-card"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={() => onViewDetail(room)}
    >
      {/* ── Image area ── */}
      <div
        className="room-image-wrapper"
        style={{ position: "relative", overflow: "hidden" }}
      >
        <Shimmer visible={!firstLoaded} />

        {cardImages.map((src, i) => (
          <SlideshowImage
            key={src || i}
            src={src}
            alt={`${room.name} view ${i + 1}`}
            index={i}
            displayed={displayed}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}

        {/* Badge on top */}
        <span className="room-badge" style={{ zIndex: 3 }}>{room.badge}</span>

        {/* Dot indicators */}
        {cardImages.length > 1 && (
          <div className="room-slide-dots" style={{ zIndex: 3 }}>
            {cardImages.map((_, i) => (
              <span
                key={i}
                className={`room-slide-dot${active === i ? " active" : ""}`}
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Text info ── */}
      <div className="room-info">
        <h3 className="room-name">{room.name}</h3>
        <p className="room-subtitle-text">{room.subtitle}</p>
        <p className="room-desc">{room.desc}</p>

        <div className="room-features">
          {room.features.map((f, i) => (
            <span className="room-feature" key={i}>
              <f.icon /> {f.text}
            </span>
          ))}
        </div>

        <div className="room-price-row">
          <div className="room-price">&#8377;{room.price} <span>/ night</span></div>
          <div className="room-action-btns">
            <button
              className="room-detail-btn"
              onClick={(e) => { e.stopPropagation(); onViewDetail(room); }}
            >
              <Eye size={14} /> Details
            </button>
            <button
              className="room-book-btn"
              onClick={(e) => { e.stopPropagation(); onBook(room.name); }}
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ROOMS SECTION
───────────────────────────────────────────────────────────────── */
const RoomsSection = ({ onBook, onViewDetail }) => (
  <section className="rooms-section" id="rooms">
    <div className="rooms-header">
      <p className="section-tag">Accommodations</p>
      <h2 className="section-title">Our Luxurious Rooms</h2>
      <p className="section-desc">
        Each room is thoughtfully designed to provide the ultimate comfort and relaxation.
      </p>
    </div>
    <div className="rooms-grid">
      {ROOMS.map((room) => (
        <RoomCard key={room.id} room={room} onBook={onBook} onViewDetail={onViewDetail} />
      ))}
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────
   AMENITIES SECTION
───────────────────────────────────────────────────────────────── */
const AmenitiesSection = () => (
  <section className="amenities-section" id="amenities">
    <div className="amenities-header">
      <p className="section-tag">Facilities</p>
      <h2 className="section-title">World-Class Amenities</h2>
      <p className="section-desc">Everything you need for a perfect stay, all under one roof.</p>
    </div>
    <div className="amenities-grid">
      {AMENITIES.map((a, i) => {
        const Icon = a.icon;
        return (
          <div className="amenity-card" key={i}>
            <div className="amenity-icon"><Icon /></div>
            <h4 className="amenity-name">{a.name}</h4>
            <p className="amenity-desc">{a.desc}</p>
          </div>
        );
      })}
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────
   GALLERY SECTION  — lazy loads with shimmer per tile
───────────────────────────────────────────────────────────────── */
const GalleryImage = ({ src, index, onClick }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className="gallery-item"
      style={{ position: "relative", overflow: "hidden", cursor: "pointer" }}
      onClick={() => onClick(index)}
    >
      <div
        className="img-shimmer"
        style={{ opacity: loaded ? 0 : 1, transition: "opacity 0.5s ease" }}
      />
      <img
        src={src}
        alt={`Gallery ${index + 1}`}
        loading="lazy"
        decoding="async"
        style={{
          width: "100%", height: "100%", objectFit: "cover",
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.55s ease",
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
      <div className="gallery-overlay">
        {/* <span className="gallery-label">Gallery {index + 1}</span> */}
      </div>
    </div>
  );
};

const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape")      onClose();
      if (e.key === "ArrowRight")  onNext();
      if (e.key === "ArrowLeft")   onPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNext, onPrev]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.92)",
        backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        animation: "lbFadeIn 0.25s ease",
      }}
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 20, right: 24,
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          color: "#fff", borderRadius: "50%",
          width: 40, height: 40, fontSize: 18,
          cursor: "pointer", zIndex: 10,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.2s",
        }}
      >✕</button>

      {/* Prev button */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        style={{
          position: "absolute", left: 20, top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          color: "#fff", borderRadius: "50%",
          width: 48, height: 48, fontSize: 22,
          cursor: "pointer", zIndex: 10,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >‹</button>

      {/* Main image */}
      <img
        src={images[currentIndex]}
        alt={`Gallery ${currentIndex + 1}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "88vw", maxHeight: "82vh",
          objectFit: "contain",
          borderRadius: 4,
          boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
          animation: "lbZoomIn 0.3s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      />

      {/* Next button */}
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        style={{
          position: "absolute", right: 20, top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          color: "#fff", borderRadius: "50%",
          width: 48, height: 48, fontSize: 22,
          cursor: "pointer", zIndex: 10,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >›</button>

      {/* Counter */}
      <div
        style={{
          position: "absolute", bottom: 60,
          left: "50%", transform: "translateX(-50%)",
          color: "rgba(255,255,255,0.5)",
          fontSize: 13, letterSpacing: 2,
        }}
      >
        {currentIndex + 1} / {images.length}
      </div>

      {/* Thumbnail strip */}
      <div
        style={{
          position: "absolute", bottom: 16,
          left: "50%", transform: "translateX(-50%)",
          display: "flex", gap: 6,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((img, i) => (
          <img
            key={i}
            src={img}
            alt=""
            onClick={() => onPrev(i)} // handled below via dedicated handler
            style={{
              width: 44, height: 30,
              objectFit: "cover",
              opacity: i === currentIndex ? 1 : 0.35,
              outline: i === currentIndex ? "1.5px solid #b8976a" : "none",
              cursor: "pointer",
              transition: "opacity 0.2s",
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes lbFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes lbZoomIn { from { transform: scale(0.92); opacity: 0 } to { transform: scale(1); opacity: 1 } }
      `}</style>
    </div>
  );
};

const GallerySection = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const images = galleryImages || [];
  const isOpen = lightboxIndex !== null;

  const open  = (i) => setLightboxIndex(i);
  const close = () => setLightboxIndex(null);
  const prev  = (i) => setLightboxIndex(
    typeof i === "number" ? i : (lightboxIndex - 1 + images.length) % images.length
  );
  const next  = () => setLightboxIndex((lightboxIndex + 1) % images.length);

  return (
    <>
      <section className="gallery-section" id="gallery">
        <div className="gallery-header">
          <p className="section-tag">Photo Gallery</p>
          <h2 className="section-title">Capture The Beauty</h2>
          <p className="section-desc">Explore our stunning property through these beautiful moments.</p>
        </div>
        <div className="gallery-grid">
          {images.map((img, i) => (
            <GalleryImage key={img || i} src={img} index={i} onClick={open} />
          ))}
        </div>
      </section>

      {isOpen && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={close}
          onPrev={prev}
          onNext={next}
        />
      )}
    </>
  );
};
/* ─────────────────────────────────────────────────────────────────
   CONTACT SECTION
───────────────────────────────────────────────────────────────── */
const ContactSection = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [sent, setSent]   = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const phoneNumber = "919404662828";
    const message = `Hello, I want to contact you:\n\nName: ${form.full_name}\nEmail: ${form.email}\nPhone: ${form.phone}\nMessage: ${form.message}`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, "_blank");
    setSent(true);
    setForm({ full_name: "", email: "", phone: "", message: "" });
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-wrapper">
        <div>
          <p className="section-tag">Get In Touch</p>
          <h2 className="section-title">Contact Us</h2>
          <p className="section-desc" style={{ marginBottom: "2.5rem" }}>
            Have questions or ready to book? We&apos;re here to help make your stay unforgettable.
          </p>
          {[
            { icon: MapPin, title: "Our Address",    text: "Dapoli - Harnai Road, Saldure, Tal. Dapoli - 415712, Maharashtra" },
            { icon: Phone,  title: "Phone Numbers",  text: "+91 9404617122  |  +91 9404662828" },
            { icon: Mail,   title: "Email Address",  text: "miranshheavenly22@gmail.com" },
          ].map(({ icon: Icon, title, text }) => (
            <div className="contact-info-card" key={title}>
              <div className="contact-icon-box"><Icon /></div>
              <div>
                <h4 className="contact-info-title">{title}</h4>
                <p className="contact-info-text">{text}</p>
              </div>
            </div>
          ))}
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <h3>Send Us a Message</h3>
          <div className="form-group">
            <label>Full Name</label>
            <input type="text" name="full_name" value={form.full_name} onChange={handleChange} required placeholder="Your full name" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="your@email.com" />
            </div>
            <div className="form-group">
              <label>Phone (Optional)</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 xxxxxxxxxx" />
            </div>
          </div>
          <div className="form-group">
            <label>Your Message</label>
            <textarea name="message" value={form.message} onChange={handleChange} required placeholder="How can we help you?" />
          </div>
          <button type="submit" className="submit-btn">Send Message</button>
          {sent && <div className="success-message">Thank you! Your message has been sent successfully.</div>}
        </form>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────
   RESERVATION MODAL
───────────────────────────────────────────────────────────────── */
const HOTEL_WHATSAPP = "919404662828";

const ReservationModal = ({ isOpen, onClose, preselectedRoom }) => {
  const todayDate    = new Date();
  const tomorrowDate = new Date();
  tomorrowDate.setDate(todayDate.getDate() + 1);
  const today    = todayDate.toISOString().split("T")[0];
  const tomorrow = tomorrowDate.toISOString().split("T")[0];

  const defaultRoom = preselectedRoom
    ? ROOMS.find((r) => r.name === preselectedRoom) ?? ROOMS[0]
    : ROOMS[0];

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    check_in: today, check_out: tomorrow,
    roomId: defaultRoom.id, extraGuests: 0, special_requests: "",
  });

  useEffect(() => {
    if (preselectedRoom) {
      const match = ROOMS.find((r) => r.name === preselectedRoom);
      if (match) setForm((prev) => ({ ...prev, roomId: match.id, extraGuests: 0 }));
    }
  }, [preselectedRoom]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const selected      = ROOMS.find((r) => r.id === Number(form.roomId)) ?? ROOMS[0];
  const defaultGuests = Number(selected.guests) || 2;
  const totalGuests   = defaultGuests + Number(form.extraGuests || 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) { alert("Please fill in your name and phone number."); return; }
    const message = `
📌 *New Booking Inquiry — Miransh Heavenly*

👤 Name: ${form.full_name}
📱 Phone: ${form.phone}
📧 Email: ${form.email}

📅 Check-in: ${form.check_in}
📅 Check-out: ${form.check_out}

🏨 Room: ${selected.name} (${selected.subtitle})
💰 Price: ₹${selected.price} / night (incl. breakfast)

👥 Guests: ${totalGuests}
(Default: ${defaultGuests}, Extra: ${form.extraGuests})

📝 Requests:
${form.special_requests || "None"}
    `.trim();
    window.open(`https://wa.me/${HOTEL_WHATSAPP}?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><X size={18} /></button>
        <p className="modal-subtitle" style={{ textAlign:"center", fontSize:11, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:4 }}>
          Miransh Heavenly
        </p>
        <h2 className="modal-title">Book Your Stay</h2>
        <p className="modal-subtitle">Fill in the details and we'll reach you on WhatsApp to confirm.</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Full Name</label>
            <input type="text" name="full_name" value={form.full_name} onChange={handleChange} required placeholder="Your full name" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="+91 xxxxxxxxxx" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Check-in</label>
              <input type="date" name="check_in" value={form.check_in} min={today} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Check-out</label>
              <input type="date" name="check_out" value={form.check_out} min={form.check_in} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-group">
            <label>Room</label>
            <select name="roomId" value={form.roomId} onChange={handleChange}>
              {ROOMS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — {r.subtitle} ({r.sharing}) · ₹{r.price}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Extra guests (above {defaultGuests})</label>
              <input type="number" name="extraGuests" min={0} value={form.extraGuests} onChange={handleChange} />
            </div>
            <div className="form-group" style={{ display:"flex", flexDirection:"column", justifyContent:"flex-end", paddingBottom:4, fontSize:13, color:"var(--color-text-light)" }}>
              <p style={{ margin:0 }}>Default: {defaultGuests}</p>
              <p style={{ margin:0 }}>Total guests: {totalGuests}</p>
            </div>
          </div>
          <div style={{ textAlign:"center", fontSize:20, fontWeight:500, color:"#136363", margin:"8px 0" }}>
            ₹ {selected.price} / night
          </div>
          <div className="form-group">
            <label>Special Requests (Optional)</label>
            <textarea name="special_requests" value={form.special_requests} onChange={handleChange} placeholder="Any special requirements..." rows={3} />
          </div>
          <button type="submit" className="submit-btn">Send Inquiry on WhatsApp</button>
        </form>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────────────────────────── */
const Footer = () => (
  <footer className="footer">
    <div className="footer-content">
      <div className="footer-brand">
        <h3>Miransh Heavenly</h3>
        <p>Where Relaxation Meets Luxury by the Sea. Located at Dapoli - Harnai Road, Saldure, Maharashtra — a sanctuary of comfort and natural beauty.</p>
      </div>
      <div className="footer-col">
        <h4>Quick Links</h4>
        <a href="#home">Home</a>
        <a href="#rooms">Rooms</a>
        <a href="#amenities">Amenities</a>
        <a href="#gallery">Gallery</a>
      </div>
      <div className="footer-col">
        <h4>Services</h4>
        <a href="#rooms">Room Booking</a>
        <a href="#amenities">Swimming Pool</a>
        <a href="#amenities">Fine Dining</a>
        <a href="#contact">Concierge</a>
      </div>
      <div className="footer-col">
        <h4>Contact</h4>
        <a href="tel:+919404617122">+91 9404617122</a>
        <a href="tel:+919404662828">+91 9404662828</a>
        <a href="mailto:miranshheavenly22@gmail.com">miranshheavenly22@gmail.com</a>
        <a href="#contact">Dapoli, Maharashtra</a>
      </div>
    </div>
    <div className="footer-bottom">
      &copy; {new Date().getFullYear()} Miransh Heavenly. All rights reserved.
    </div>
  </footer>
);

/* ─────────────────────────────────────────────────────────────────
   MAIN APP
───────────────────────────────────────────────────────────────── */
function MiranshApp() {
  const [modalOpen,    setModalOpen]    = useState(false);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [detailRoom,   setDetailRoom]   = useState(null);

  function MiranshApp() {
  // ✅ Add this — resets scroll when page loads via navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ... rest of your code
}

  const openBooking = useCallback((roomName) => {
    setSelectedRoom(roomName || "");
    setModalOpen(true);
  }, []);

  const openDetail = useCallback((room) => {
    setDetailRoom(room);
    window.history.pushState({ detailOpen: true }, "");
  }, []);

  const closeDetail = useCallback(() => {
    setDetailRoom(null);
  }, []);

  useEffect(() => {
    if (!detailRoom) return;
    const handlePopState = () => setDetailRoom(null);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [detailRoom]);

return (
  
    <div className="miransh-page">   
      <Helmet>
        <title>Miransh Heavenly Resort | Dreamland Inn Beach Resort</title>
        <meta
          name="description"
          content="Stay at Miransh Heavenly Resort by Dreamland Inn. Luxury rooms, beach view, AC rooms, best stay experience."
        />
      </Helmet>

      <Navbar onBookClick={() => openBooking()} />
      <HeroSection onBookClick={() => openBooking()} />
        
      <RoomsSection onBook={openBooking} onViewDetail={openDetail} />
      <AmenitiesSection />
      <GallerySection />
      <ContactSection />
      <Footer />

      <ReservationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        preselectedRoom={selectedRoom}
      />

      {detailRoom && (
        <RoomDetailPage
          room={detailRoom}
          onClose={closeDetail}
          onBook={openBooking}
        />
      )}
    </div>
  );
}

export default MiranshApp;